import { MenuItem } from 'components/dropdowns/RMDropdown';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { useRightClick } from '../rightClickProvider';
import { useCallback } from 'react';
import {
  ERightClickAction,
  KeyboardShortcutsLables,
} from 'components/customComponents/TreeControl/TreeControlHeader/constants';
import EMultiSelectionTypes from 'enums/EMultiSelectionTypes';
import { getPoiExportSubmenuItems } from 'components/Experiment/utils';

const useSetNodeRightClick = () => {
  const { replaceMenuItems } = useRightClick();

  const getNodeCommonOptions = useCallback(
    (actionMap, isExplorerNode, isProject: boolean = false): MenuItem[] => {
      return [
        !isExplorerNode && {
          label: ERightClickAction.Open,
          onClick: actionMap[ERightClickAction.Open]?.action,
          isDisabled: actionMap[ERightClickAction.Open]?.isDisabled,
        },
        isExplorerNode && {
          label: ERightClickAction.NewFolder,
          onClick: actionMap[ERightClickAction.NewFolder]?.action,
          isDisabled: actionMap[ERightClickAction.NewFolder]?.isDisabled,
        },
        isExplorerNode && {
          label: isProject ? ERightClickAction.NewProject : ERightClickAction.NewScreen,
          onClick:
            actionMap[
              isProject ? ERightClickAction.NewProject : ERightClickAction.NewScreen
            ]?.action,
          isDisabled:
            actionMap[
              isProject ? ERightClickAction.NewProject : ERightClickAction.NewScreen
            ]?.isDisabled,
        },
      ].filter(Boolean);
    },
    [],
  );

  const getClipboardNodeOptions = useCallback(
    (actionMap, isExplorerNode, isContextBackground: boolean = false): MenuItem[] => {
      const isBg = !!isContextBackground;
      return [
        {
          label: ERightClickAction.Cut,
          subLabel: KeyboardShortcutsLables.Cut,
          onClick: actionMap[ERightClickAction.Cut]?.action,
          isDisabled: isBg || actionMap[ERightClickAction.Cut]?.isDisabled,
        },
        {
          label: ERightClickAction.Copy,
          subLabel: KeyboardShortcutsLables.Copy,
          onClick: actionMap[ERightClickAction.Copy]?.action,
          isDisabled: isBg || actionMap[ERightClickAction.Copy]?.isDisabled,
        },
        isExplorerNode && {
          label: ERightClickAction.Paste,
          subLabel: KeyboardShortcutsLables.Paste,
          onClick: actionMap[ERightClickAction.Paste]?.action,
          isDisabled: actionMap[ERightClickAction.Paste]?.isDisabled,
        },
        {
          label: ERightClickAction.Delete,
          subLabel: KeyboardShortcutsLables.Delete,
          onClick: actionMap[ERightClickAction.Delete]?.action,
          isDisabled: isBg || actionMap[ERightClickAction.Delete]?.isDisabled,
        },
        {
          label: ERightClickAction.Rename,
          subLabel: KeyboardShortcutsLables.Rename,
          onClick: actionMap[ERightClickAction.Rename]?.action,
          isDisabled: isBg || actionMap[ERightClickAction.Rename]?.isDisabled,
        },
      ].filter(Boolean);
    },
    [],
  );

  const getScreensNodeOptions = useCallback(
    (actionMap, isExplorerNode): MenuItem[] => {
      return [
        ...getNodeCommonOptions(actionMap, isExplorerNode),
        isExplorerNode && {
          label: ERightClickAction.Paste,
          onClick: actionMap[ERightClickAction.Paste]?.action,
          isDisabled: actionMap[ERightClickAction.Paste]?.isDisabled,
        },
        {
          label: ERightClickAction.Analyze,
          onClick: actionMap[ERightClickAction.Analyze]?.action,
          submenuItems: actionMap[ERightClickAction.Analyze]?.subMenu,
        },
      ];
    },
    [getNodeCommonOptions],
  );

  const getFolderInScreensNodeOptions = useCallback(
    (actionMap, isExplorerNode, isContextBackground: boolean = false): MenuItem[] => {
      return [
        ...getNodeCommonOptions(actionMap, isExplorerNode),
        ...getClipboardNodeOptions(actionMap, isExplorerNode, isContextBackground),
        {
          label: ERightClickAction.Analyze,
          onClick: actionMap[ERightClickAction.Analyze]?.action,
          submenuItems: actionMap[ERightClickAction.Analyze]?.subMenu,
        },
      ];
    },
    [getNodeCommonOptions, getClipboardNodeOptions],
  );

  const getProjectsNodeOptions = useCallback(
    (actionMap, isExplorerNode): MenuItem[] => {
      return [
        ...getNodeCommonOptions(actionMap, isExplorerNode, true),
        isExplorerNode && {
          label: ERightClickAction.Paste,
          onClick: actionMap[ERightClickAction.Paste]?.action,
          isDisabled: actionMap[ERightClickAction.Paste]?.isDisabled,
        },
        {
          label: ERightClickAction.Analyze,
          onClick: actionMap[ERightClickAction.Analyze]?.action,
          submenuItems: actionMap[ERightClickAction.Analyze]?.subMenu,
        },
      ].filter(Boolean);
    },
    [getNodeCommonOptions],
  );

  const getFolderInProjectsNodeOptions = useCallback(
    (actionMap, isExplorerNode, isContextBackground: boolean = false): MenuItem[] => {
      return [
        ...getNodeCommonOptions(actionMap, isExplorerNode, true),
        ...getClipboardNodeOptions(actionMap, isExplorerNode, isContextBackground),
        {
          label: ERightClickAction.Analyze,
          onClick: actionMap[ERightClickAction.Analyze]?.action,
          submenuItems: actionMap[ERightClickAction.Analyze]?.subMenu,
        },
      ].filter(Boolean);
    },
    [getNodeCommonOptions, getClipboardNodeOptions],
  );

  const getFolderInProjectNodeOptions = useCallback(
    (
      actionMap,
      isExplorerNode,
      showProject: boolean = false,
      isContextBackground: boolean = false,
    ): MenuItem[] => {
      return [
        ...getNodeCommonOptions(actionMap, isExplorerNode, showProject),
        isExplorerNode && {
          label: ERightClickAction.NewProteinFormulation,
          onClick: actionMap[ERightClickAction.NewProteinFormulation]?.action,
          isDisabled: actionMap[ERightClickAction.NewProteinFormulation]?.isDisabled,
        },
        isExplorerNode && {
          label: ERightClickAction.NewExperiment,
          onClick: actionMap[ERightClickAction.NewExperiment]?.action,
          isDisabled: actionMap[ERightClickAction.NewExperiment]?.isDisabled,
        },
        isExplorerNode && {
          label: ERightClickAction.NewFragmentScreen,
          onClick: actionMap[ERightClickAction.NewFragmentScreen]?.action,
          isDisabled: actionMap[ERightClickAction.NewFragmentScreen]?.isDisabled,
        },
        ...getClipboardNodeOptions(actionMap, isExplorerNode, isContextBackground),
        {
          label: ERightClickAction.Analyze,
          onClick: actionMap[ERightClickAction.Analyze]?.action,
          submenuItems: actionMap[ERightClickAction.Analyze]?.subMenu,
        },
      ].filter(Boolean);
    },
    [getNodeCommonOptions, getClipboardNodeOptions],
  );

  const getSynchTripNodeOptions = useCallback((actionMap): MenuItem[] => {
    return [
      {
        label: ERightClickAction.NewFolder,
        onClick: actionMap[ERightClickAction.NewFolder]?.action,
      },
      {
        label: ERightClickAction.NewSynchrotronTrip,
        onClick: actionMap[ERightClickAction.NewSynchrotronTrip]?.action,
        isDisabled: actionMap[ERightClickAction.NewSynchrotronTrip]?.isDisabled,
      },
      {
        label: ERightClickAction.Paste,
        onClick: actionMap[ERightClickAction.Paste]?.action,
        isDisabled: actionMap[ERightClickAction.Paste]?.isDisabled,
      },
    ];
  }, []);

  const getSearchesNodeOptions = useCallback((actionMap): MenuItem[] => {
    return [
      {
        label: ERightClickAction.NewFolder,
        onClick: actionMap[ERightClickAction.NewSearchFolder]?.action,
        isDisabled: actionMap[ERightClickAction.NewSearchFolder]?.isDisabled,
      },
      {
        label: ERightClickAction.NewSearchAndFilter,
        onClick: actionMap[ERightClickAction.NewSearchAndFilter]?.action,
        isDisabled: actionMap[ERightClickAction.NewSearchAndFilter]?.isDisabled,
      },
      {
        label: ERightClickAction.Paste,
        onClick: actionMap[ERightClickAction.Paste]?.action,
        isDisabled: actionMap[ERightClickAction.Paste]?.isDisabled,
      },
    ];
  }, []);

  const getSearchesFolderNodeOptions = useCallback(
    (actionMap, isExplorerNode, isContextBackground: boolean = false): MenuItem[] => {
      return [
        !isExplorerNode && {
          label: ERightClickAction.Open,
          onClick: actionMap[ERightClickAction.Open]?.action,
          isDisabled: actionMap[ERightClickAction.Open]?.isDisabled,
        },
        isExplorerNode && {
          label: ERightClickAction.NewFolder,
          onClick: actionMap[ERightClickAction.NewSearchFolder]?.action,
          isDisabled: actionMap[ERightClickAction.NewSearchFolder]?.isDisabled,
        },
        isExplorerNode && {
          label: ERightClickAction.NewSearchAndFilter,
          onClick: actionMap[ERightClickAction.NewSearchAndFilter]?.action,
          isDisabled: actionMap[ERightClickAction.NewSearchAndFilter]?.isDisabled,
        },
        ...getClipboardNodeOptions(actionMap, isExplorerNode, isContextBackground),
      ].filter(Boolean);
    },
    [],
  );

  const getSearchAndFilterNodeOptions = useCallback((actionMap): MenuItem[] => {
    return [
      {
        label: ERightClickAction.Open,
        onClick: actionMap[ERightClickAction.Open]?.action,
        isDisabled: actionMap[ERightClickAction.Open]?.isDisabled,
      },
      ...getClipboardNodeOptions(actionMap, false),
    ].filter(Boolean);
  }, []);

  const getExperimentNodeOptions = useCallback(
    (actionMap, isExplorerNode, showPrintBarcodeInTreeMenu: boolean): MenuItem[] => {
      return [
        {
          label: ERightClickAction.Open,
          onClick: actionMap[ERightClickAction.Open]?.action,
          isDisabled: actionMap[ERightClickAction.Open]?.isDisabled,
        },
        ...getClipboardNodeOptions(actionMap, isExplorerNode),
        {
          label: ERightClickAction.MarkAsNotSeen,
          onClick: actionMap[ERightClickAction.MarkAsNotSeen]?.action,
          isDisabled: actionMap[ERightClickAction.MarkAsNotSeen]?.isDisabled,
        },
        {
          label: ERightClickAction.Export,
          onClick: actionMap[ERightClickAction.Export]?.action,
          submenuItems: actionMap[ERightClickAction.Export]?.subMenu,
        },
        {
          label: ERightClickAction.ExperimentReport,
          onClick: actionMap[ERightClickAction.ExperimentReport]?.action,
          isDisabled: actionMap[ERightClickAction.ExperimentReport]?.isDisabled,
        },
        {
          label: ERightClickAction.ImageNow,
          onClick: actionMap[ERightClickAction.ImageNow]?.action,
          isDisabled: actionMap[ERightClickAction.ImageNow]?.isDisabled,
        },
        {
          label: ERightClickAction.SendToAutoScore,
          onClick: actionMap[ERightClickAction.SendToAutoScore]?.action,
          isDisabled: actionMap[ERightClickAction.SendToAutoScore]?.isDisabled,
        },
        ...(showPrintBarcodeInTreeMenu
          ? [
              {
                label: ERightClickAction.PrintBarcode,
                onClick: actionMap[ERightClickAction.PrintBarcode]?.action,
                isDisabled: actionMap[ERightClickAction.PrintBarcode]?.isDisabled,
              },
            ]
          : []),
        {
          label: ERightClickAction.HitsReport,
          onClick: actionMap[ERightClickAction.HitsReport]?.action,
          isDisabled: actionMap[ERightClickAction.HitsReport]?.isDisabled,
        },
        {
          label: ERightClickAction.Analyze,
          onClick: actionMap[ERightClickAction.Analyze]?.action,
          submenuItems: actionMap[ERightClickAction.Analyze]?.subMenu,
        },
      ];
    },
    [],
  );

  const getProteinNodeOptions = useCallback((actionMap, isExplorerNode): MenuItem[] => {
    return [
      {
        label: ERightClickAction.Open,
        onClick: actionMap[ERightClickAction.Open]?.action,
      },
      {
        label: ERightClickAction.NewProteinVariant,
        onClick: actionMap[ERightClickAction.NewProteinVariant]?.action,
      },
      ...getClipboardNodeOptions(actionMap, isExplorerNode),
      {
        label: ERightClickAction.Analyze,
        onClick: actionMap[ERightClickAction.Analyze]?.action,
        submenuItems: actionMap[ERightClickAction.Analyze]?.subMenu,
      },
    ].filter(Boolean);
  }, []);

  const getSeedNodeOptions = useCallback((actionMap, isExplorerNode): MenuItem[] => {
    return [
      {
        label: ERightClickAction.Open,
        onClick: actionMap[ERightClickAction.Open]?.action,
      },
      ...getClipboardNodeOptions(actionMap, isExplorerNode),
    ].filter(Boolean);
  }, []);

  const getSynchTripOptions = useCallback((actionMap, isExplorerNode): MenuItem[] => {
    return [
      {
        label: ERightClickAction.Open,
        onClick: actionMap[ERightClickAction.Open]?.action,
      },
      ...getClipboardNodeOptions(actionMap, isExplorerNode),
      {
        label: ERightClickAction.SynchrotronTripReport,
        onClick: actionMap[ERightClickAction.SynchrotronTripReport]?.action,
        isDisabled: actionMap[ERightClickAction.SynchrotronTripReport]?.isDisabled,
      },
    ];
  }, []);

  const getScreenOptions = useCallback((actionMap, isExplorerNode): MenuItem[] => {
    return [
      {
        label: ERightClickAction.Open,
        onClick: actionMap[ERightClickAction.Open]?.action,
      },
      ...getClipboardNodeOptions(actionMap, isExplorerNode),
      {
        label: ERightClickAction.ExportToXmlFile,
        onClick: actionMap[ERightClickAction.ExportToXmlFile]?.action,
        isDisabled: actionMap[ERightClickAction.ExportToXmlFile]?.isDisabled,
      },
      {
        label: ERightClickAction.ScreenStocksReport,
        onClick: actionMap[ERightClickAction.ScreenStocksReport]?.action,
      },
      {
        label: ERightClickAction.RotateCanvas,
        onClick: actionMap[ERightClickAction.RotateCanvas]?.action,
        isDisabled: actionMap[ERightClickAction.RotateCanvas]?.isDisabled,
      },
      {
        label: ERightClickAction.Analyze,
        onClick: actionMap[ERightClickAction.Analyze]?.action,
        submenuItems: actionMap[ERightClickAction.Analyze]?.subMenu,
      },
    ];
  }, []);

  const getFragmentScreenOptions = useCallback(
    (actionMap, isExplorerNode): MenuItem[] => {
      return [
        {
          label: ERightClickAction.Open,
          onClick: actionMap[ERightClickAction.Open]?.action,
        },
        ...getClipboardNodeOptions(actionMap, isExplorerNode),
      ];
    },
    [],
  );

  const getSynchTripFolderOptions = useCallback(
    (actionMap, isExplorerNode, isContextBackground: boolean = false): MenuItem[] => {
      return [
        !isExplorerNode && {
          label: ERightClickAction.Open,
          onClick: actionMap[ERightClickAction.Open]?.action,
          isDisabled: actionMap[ERightClickAction.Open]?.isDisabled,
        },
        isExplorerNode && {
          label: ERightClickAction.NewFolder,
          onClick: actionMap[ERightClickAction.NewFolder]?.action,
        },
        isExplorerNode && {
          label: ERightClickAction.NewSynchrotronTrip,
          onClick: actionMap[ERightClickAction.NewSynchrotronTrip]?.action,
          isDisabled: actionMap[ERightClickAction.NewSynchrotronTrip]?.isDisabled,
        },
        ...getClipboardNodeOptions(actionMap, isExplorerNode, isContextBackground),
      ].filter(Boolean);
    },
    [],
  );

  //need to come up with better approach, than just passing isExplorerNode in all methods as its redundant.
  return useCallback(
    (
      actionMap,
      nodeType: ETreeNodeTypes,
      isExplorerNode: boolean = false,
      isContextBackground: boolean = false,
      showPrintBarcodeInTreeMenu: boolean = true,
    ) => {
      const getMenuItemsWithRefresh = (menuItems: MenuItem[] = []) => {
        if (!isExplorerNode) {
          return menuItems;
        }

        const refreshItem: MenuItem = {
          label: ERightClickAction.Refresh,
          onClick: actionMap[ERightClickAction.Refresh]?.action,
          isDisabled: actionMap[ERightClickAction.Refresh]?.isDisabled,
        };

        // Place Refresh immediately before Cut (clipboard section), not at the top.
        const cutIndex = menuItems.findIndex(
          (item) => item?.label === ERightClickAction.Cut,
        );
        if (cutIndex === -1) {
          const pasteIndex = menuItems.findIndex(
            (item) => item?.label === ERightClickAction.Paste,
          );
          if (pasteIndex === -1) {
            return [...menuItems, refreshItem];
          }
          return [
            ...menuItems.slice(0, pasteIndex),
            refreshItem,
            ...menuItems.slice(pasteIndex),
          ];
        }

        return [
          ...menuItems.slice(0, cutIndex),
          refreshItem,
          ...menuItems.slice(cutIndex),
        ];
      };

      let menuItems: MenuItem[] = [];
      switch (nodeType) {
        case ETreeNodeTypes.Screens:
          menuItems = getScreensNodeOptions(actionMap, isExplorerNode);
          break;
        case ETreeNodeTypes.ScreensFolder:
          menuItems = getFolderInScreensNodeOptions(
            actionMap,
            isExplorerNode,
            isContextBackground,
          );
          break;
        case ETreeNodeTypes.Projects:
          menuItems = getProjectsNodeOptions(actionMap, isExplorerNode);
          break;
        case ETreeNodeTypes.ProjectsFolder:
          menuItems = getFolderInProjectsNodeOptions(
            actionMap,
            isExplorerNode,
            isContextBackground,
          );
          break;
        case ETreeNodeTypes.Project:
          menuItems = getFolderInProjectNodeOptions(
            actionMap,
            isExplorerNode,
            false,
            isContextBackground,
          );
          break;
        case ETreeNodeTypes.ProjectFolder:
          menuItems = getFolderInProjectNodeOptions(
            actionMap,
            isExplorerNode,
            false,
            isContextBackground,
          );
          break;
        case ETreeNodeTypes.CrystalTrackv4:
          menuItems = getSynchTripNodeOptions(actionMap);
          break;
        case ETreeNodeTypes.Searches:
          menuItems = getSearchesNodeOptions(actionMap);
          break;
        case ETreeNodeTypes.SearchAnalysis:
          menuItems = getSearchAndFilterNodeOptions(actionMap);
          break;
        case ETreeNodeTypes.SearchesFolder:
          menuItems = getSearchesFolderNodeOptions(
            actionMap,
            isExplorerNode,
            isContextBackground,
          );
          break;
        case ETreeNodeTypes.Experiment:
          menuItems = getExperimentNodeOptions(
            actionMap,
            isExplorerNode,
            showPrintBarcodeInTreeMenu,
          );
          break;
        case ETreeNodeTypes.Protein:
          menuItems = getProteinNodeOptions(actionMap, isExplorerNode);
          break;
        case ETreeNodeTypes.Seed:
          menuItems = getSeedNodeOptions(actionMap, isExplorerNode);
          break;
        case ETreeNodeTypes.SynchrotronTripv4:
          menuItems = getSynchTripOptions(actionMap, isExplorerNode);
          break;
        case ETreeNodeTypes.Screen:
          menuItems = getScreenOptions(actionMap, isExplorerNode);
          break;
        case ETreeNodeTypes.FragmentScreen:
          menuItems = getFragmentScreenOptions(actionMap, isExplorerNode);
          break;
        case ETreeNodeTypes.SynchrotronTripv4Folder:
          menuItems = getSynchTripFolderOptions(
            actionMap,
            isExplorerNode,
            isContextBackground,
          );
          break;
        default:
          menuItems = [];
          break;
      }

      replaceMenuItems(getMenuItemsWithRefresh(menuItems));
    },
    [
      replaceMenuItems,
      getScreensNodeOptions,
      getFolderInScreensNodeOptions,
      getProjectsNodeOptions,
      getFolderInProjectNodeOptions,
      getSynchTripNodeOptions,
      getExperimentNodeOptions,
      getClipboardNodeOptions,
      getFolderInProjectsNodeOptions,
      getSearchesNodeOptions,
      getNodeCommonOptions,
      getProteinNodeOptions,
      getSeedNodeOptions,
      getSynchTripOptions,
      getScreenOptions,
      getFragmentScreenOptions,
      getSynchTripFolderOptions,
    ],
  );
};

export const useSetNodeMultiSelectRightClick = () => {
  const { replaceMenuItems } = useRightClick();

  const getExperimentsOptions = useCallback(
    (actionMap, showPrintBarcodeInTreeMenu: boolean): MenuItem[] => {
      return [
        {
          label: ERightClickAction.Cut,
          onClick: actionMap[ERightClickAction.Cut]?.action,
          isDisabled: actionMap[ERightClickAction.Cut]?.isDisabled,
          subLabel: KeyboardShortcutsLables.Cut,
        },
        {
          label: ERightClickAction.Copy,
          onClick: actionMap[ERightClickAction.Copy]?.action,
          isDisabled: actionMap[ERightClickAction.Copy]?.isDisabled,
          subLabel: KeyboardShortcutsLables.Copy,
        },
        {
          label: ERightClickAction.Delete,
          onClick: actionMap[ERightClickAction.Delete]?.action,
          isDisabled: actionMap[ERightClickAction.Delete]?.isDisabled,
          subLabel: KeyboardShortcutsLables.Delete,
        },
        {
          label: ERightClickAction.SendToAutoScore,
          onClick: actionMap[ERightClickAction.SendToAutoScore]?.action,
          isDisabled: actionMap[ERightClickAction.SendToAutoScore]?.isDisabled,
        },
        {
          label: ERightClickAction.ChangeSchedule,
          onClick: actionMap[ERightClickAction.ChangeSchedule]?.action,
          isDisabled: actionMap[ERightClickAction.ChangeSchedule]?.isDisabled,
        },
        ...(showPrintBarcodeInTreeMenu
          ? [
              {
                label: ERightClickAction.PrintBarcode,
                onClick: actionMap[ERightClickAction.PrintBarcode]?.action,
                isDisabled: actionMap[ERightClickAction.PrintBarcode]?.isDisabled,
              },
            ]
          : []),
        {
          label: ERightClickAction.HitsReport,
          onClick: actionMap[ERightClickAction.HitsReport]?.action,
          isDisabled: actionMap[ERightClickAction.HitsReport]?.isDisabled,
        },
        {
          label: ERightClickAction.ExperimentReport,
          onClick: actionMap[ERightClickAction.ExperimentReport]?.action,
          isDisabled: actionMap[ERightClickAction.ExperimentReport]?.isDisabled,
        },
        {
          label: ERightClickAction.ImageNow,
          onClick: actionMap[ERightClickAction.ImageNow]?.action,
          isDisabled: actionMap[ERightClickAction.ImageNow]?.isDisabled,
        },
        {
          label: ERightClickAction.ExportPOI,
          submenuItems: getPoiExportSubmenuItems(
            actionMap[ERightClickAction.ExportPOI]?.action ?? (() => {}),
          ),
        },
        {
          label: ERightClickAction.Analyze,
          onClick: actionMap[ERightClickAction.Analyze]?.action,
          submenuItems: actionMap[ERightClickAction.Analyze]?.subMenu,
        },
      ].filter(Boolean);
    },
    [],
  );

  const getFilesOptions = useCallback(
    (actionMap, isAnalyze: boolean = true): MenuItem[] => {
      return [
        {
          label: ERightClickAction.Cut,
          onClick: actionMap[ERightClickAction.Cut]?.action,
          isDisabled: actionMap[ERightClickAction.Cut]?.isDisabled,
          subLabel: KeyboardShortcutsLables.Cut,
        },
        {
          label: ERightClickAction.Copy,
          onClick: actionMap[ERightClickAction.Copy]?.action,
          isDisabled: actionMap[ERightClickAction.Copy]?.isDisabled,
          subLabel: KeyboardShortcutsLables.Copy,
        },
        {
          label: ERightClickAction.Delete,
          onClick: actionMap[ERightClickAction.Delete]?.action,
          isDisabled: actionMap[ERightClickAction.Delete]?.isDisabled,
          subLabel: KeyboardShortcutsLables.Delete,
        },
        isAnalyze && {
          label: ERightClickAction.Analyze,
          onClick: actionMap[ERightClickAction.Analyze]?.action,
          submenuItems: actionMap[ERightClickAction.Analyze]?.subMenu,
        },
      ].filter(Boolean);
    },
    [],
  );

  return useCallback(
    (
      actionMap: any,
      nodesType: EMultiSelectionTypes,
      isAnalyze: boolean = true,
      showPrintBarcodeInTreeMenu: boolean = true,
    ) => {
      switch (nodesType) {
        case EMultiSelectionTypes.Experiment:
          replaceMenuItems(getExperimentsOptions(actionMap, showPrintBarcodeInTreeMenu));
          break;
        case EMultiSelectionTypes.Files:
          replaceMenuItems(getFilesOptions(actionMap, isAnalyze));
          break;
        default:
          replaceMenuItems([]);
          break;
      }
    },
    [replaceMenuItems, getExperimentsOptions, getFilesOptions],
  );
};

export default useSetNodeRightClick;
